import { apiFetch, getAccessTokenAsync } from './auth';
import { buildApiUrl, API_CONFIG } from '../config';

// Cache für 5 Minuten (300.000 ms)
const CACHE_DURATION = 5 * 60 * 1000;

// Cache für Standorte
let locationsCache: { data: any[]; timestamp: number; orgID: number } | null = null;

// Cache für Funkkanäle
let radioChannelsCache: { data: any[]; timestamp: number; orgID: number } | null = null;

// Cache für Operations-Typen
let operationsTypesCache: { data: any[]; timestamp: number; orgID: number } | null = null;

// Cache für Tasks
let tasksCache: { data: any[]; timestamp: number; orgID: number } | null = null;

// Cache für Feedbacks
let feedbacksCache: { data: any[]; timestamp: number; orgID: number } | null = null;

// Hilfsfunktion zum Prüfen, ob Cache gültig ist
const isCacheValid = (cache: { data: any[]; timestamp: number; orgID: number } | null): boolean => {
  return cache !== null && (Date.now() - cache.timestamp) < CACHE_DURATION;
};

// Cache löschen (exportiert, damit z.B. das Einstellungs-Modal frische Daten laden kann)
export const clearCache = () => {
  locationsCache = null;
  radioChannelsCache = null;
  operationsTypesCache = null;
  tasksCache = null;
  feedbacksCache = null;
};

// OperationsTypes Cache explizit löschen
export const clearOperationsTypesCache = () => {
  operationsTypesCache = null;
};

// Hilfsfunktionen um orgID und userID aus dem JWT Token zu extrahieren
const getOrgID = async (): Promise<number | null> => {
  // WICHTIG: Priorisiere immer die ausgewählte Organisation aus localStorage
  const selectedOrgID = localStorage.getItem('selectedOrgID');
  if (selectedOrgID) {
    const parsedSelectedOrgID = parseInt(selectedOrgID);
    if (!isNaN(parsedSelectedOrgID)) {
      console.log('🏢 getOrgID: Verwende ausgewählte Organisation:', parsedSelectedOrgID);
      return parsedSelectedOrgID;
    }
  }
  
  // Fallback: Hole orgID aus dem JWT-Token
  const accessToken = await getAccessTokenAsync();
  if (accessToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      console.log('🏢 getOrgID: Fallback auf JWT-Organisation:', payload.orgID);
      return payload.orgID;
    } catch {}
  }
  return null;
};

// Neue Funktion: Cache bei Organisationswechsel löschen
export const clearCacheOnOrgChange = async () => {
  const currentOrgID = await getOrgID();
  
  // Wenn sich die Organisation geändert hat, Cache löschen
  if (currentOrgID && (
    (locationsCache && locationsCache.orgID !== currentOrgID) ||
    (radioChannelsCache && radioChannelsCache.orgID !== currentOrgID) ||
    (operationsTypesCache && operationsTypesCache.orgID !== currentOrgID) ||
    (tasksCache && tasksCache.orgID !== currentOrgID)
  )) {
    console.log('🔄 Organisation gewechselt, lösche Cache...');
    clearCache();
  }
};

const getUserID = async (): Promise<number | null> => {
  const accessToken = await getAccessTokenAsync();
  if (accessToken) {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      return payload.id;
    } catch {}
  }
  return null;
};

// Standorte synchronisieren
export const syncLocations = async (): Promise<any[] | null> => {
  try {
    const orgID = await getOrgID();
    if (!orgID) {
      console.error('No orgID available for locations sync');
      return null;
    }

    // Prüfen, ob Cache gültig ist UND für die aktuelle Organisation
    if (locationsCache && isCacheValid(locationsCache) && locationsCache.orgID === orgID) {
      return locationsCache.data;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOCATIONS), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID: [orgID] })
    });

    if (response.ok) {
      const data = await response.json();
      locationsCache = { data, timestamp: Date.now(), orgID };
      
      // Lokalen Cache aktualisieren
      localStorage.setItem('locationsWithIds', JSON.stringify(data));
      localStorage.setItem('standardStandorte', JSON.stringify(data.map((l: any) => l.name)));
      
      return data;
    } else {
      const errorText = await response.text();
      console.error('Failed to sync locations:', response.status, errorText);
      return null;
    }
  } catch (error) {
    console.error('Error syncing locations:', error);
    return null;
  }
};

// Funkkanäle synchronisieren
export const syncRadioChannels = async (): Promise<any[] | null> => {
  try {
    // Prüfen, ob Cache gültig ist
    if (isCacheValid(radioChannelsCache)) {
      return radioChannelsCache!.data;
    }

    const orgID = await getOrgID();
    if (!orgID) {
      console.error('No orgID available for radio channels sync');
      return null;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.RADIOCHANNELS), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID: [orgID] })
    });

    if (response.ok) {
      const data = await response.json();
      radioChannelsCache = { data, timestamp: Date.now(), orgID };
      
      // Lokalen Cache aktualisieren
      localStorage.setItem('radioChannelsWithIds', JSON.stringify(data));
      localStorage.setItem('standardFunkkanaele', JSON.stringify(data.map((c: any) => c.name)));
      
      return data;
    } else {
      console.error('Failed to sync radio channels:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Error syncing radio channels:', error);
    return null;
  }
};

// Feedbacks synchronisieren
export const syncFeedbacks = async (): Promise<any[] | null> => {
  try {
    // Prüfen, ob Cache gültig ist
    if (isCacheValid(feedbacksCache)) {
      return feedbacksCache!.data;
    }

    const orgID = await getOrgID();
    if (!orgID) {
      console.error('No orgID available for feedbacks sync');
      return null;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.FEEDBACKS), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID: [orgID] })
    });

    if (response.ok) {
      const data = await response.json();
      feedbacksCache = { data, timestamp: Date.now(), orgID };
      
      // Lokalen Cache aktualisieren
      localStorage.setItem('feedbacksWithIds', JSON.stringify(data));
      localStorage.setItem('standardFeedbacks', JSON.stringify(data.map((f: any) => f.text)));
      
      return data;
    } else {
      console.error('Failed to sync feedbacks:', response.status);
      return null;
    }
  } catch (error) {
    console.error('Error syncing feedbacks:', error);
    return null;
  }
};

// Operations-Typen synchronisieren
export const syncOperationsTypes = async (): Promise<any[] | null> => {
  try {
    console.log('🔄 syncOperationsTypes aufgerufen');
    
    // Prüfen, ob Cache gültig ist
    if (isCacheValid(operationsTypesCache)) {
      console.log('✅ Cache gültig, verwende gecachte Daten:', operationsTypesCache!.data);
      return operationsTypesCache!.data;
    }

    const orgID = await getOrgID();
    console.log('🔍 Extrahierte orgID:', orgID);
    
    if (!orgID) {
      console.error('❌ No orgID available for operations types sync');
      return null;
    }

    const url = buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_BY_ORG);
    const requestBody = { orgID };
    console.log('🌐 API-Call:', url);
    console.log('📦 Request Body:', requestBody);

    const response = await apiFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    console.log('📡 Response Status:', response.status);
    console.log('📡 Response OK:', response.ok);

    if (response.ok) {
      const data = await response.json();
      console.log('✅ API Response erfolgreich:', data);
      console.log('📊 Anzahl Operations-Typen:', data.length);
      
      operationsTypesCache = { data, timestamp: Date.now(), orgID };
      console.log('💾 Cache aktualisiert');
      
      return data;
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to sync operations types:', response.status, errorText);
      return null;
    }
  } catch (error) {
    console.error('❌ Error syncing operations types:', error);
    return null;
  }
};

// Standort hinzufügen mit Cache-Clear
export const addLocationWithCacheClear = async (name: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    const userID = await getUserID();
    
    if (!orgID || !userID) {
      console.error('No orgID or userID available for adding location');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOCATIONS_ADD), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        orgID, 
        name, 
        userID 
      })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to add location:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error adding location:', error);
    return false;
  }
};

// Funkkanal hinzufügen mit Cache-Clear
export const addRadioChannelWithCacheClear = async (name: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    const userID = await getUserID();
    
    if (!orgID || !userID) {
      console.error('No orgID or userID available for adding radio channel');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.RADIOCHANNELS_ADD), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        orgID, 
        name, 
        userID 
      })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to add radio channel:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error adding radio channel:', error);
    return false;
  }
};

// Standort löschen mit Cache-Clear
export const deleteLocationWithCacheClear = async (id: number, name: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for deleting location');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOCATIONS_DELETE(id)), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else if (response.status === 404) {
      // 404 bedeutet, dass der Standort nicht gefunden wurde (möglicherweise bereits gelöscht)
      // Das behandeln wir als erfolgreich, da das Ziel (Standort entfernen) erreicht ist
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to delete location:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error deleting location:', error);
    return false;
  }
};

// Funkkanal löschen mit Cache-Clear
export const deleteRadioChannelWithCacheClear = async (id: number, name: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for deleting radio channel');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.RADIOCHANNELS_DELETE(id)), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else if (response.status === 404) {
      // 404 bedeutet, dass der Funkkanal nicht gefunden wurde (möglicherweise bereits gelöscht)
      // Das behandeln wir als erfolgreich, da das Ziel (Funkkanal entfernen) erreicht ist
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to delete radio channel:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error deleting radio channel:', error);
    return false;
  }
};

// Feedback hinzufügen mit Cache-Clear
export const addFeedbackWithCacheClear = async (text: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    const userID = await getUserID();
    
    if (!orgID || !userID) {
      console.error('No orgID or userID available for adding feedback');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.FEEDBACKS_ADD), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        orgID, 
        text, 
        userID 
      })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to add feedback:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error adding feedback:', error);
    return false;
  }
};

// Feedback löschen mit Cache-Clear
export const deleteFeedbackWithCacheClear = async (id: number, text: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for deleting feedback');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.FEEDBACKS_DELETE(id)), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID })
    });

    if (response.ok) {
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else if (response.status === 404) {
      // 404 bedeutet, dass das Feedback nicht gefunden wurde (möglicherweise bereits gelöscht)
      // Das behandeln wir als erfolgreich, da das Ziel (Feedback entfernen) erreicht ist
      clearCache(); // Cache löschen für nächste Synchronisation
      return true;
    } else {
      console.error('Failed to delete feedback:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error deleting feedback:', error);
    return false;
  }
};

// Alle Einsatztypen laden (für Einstellungen)
export const syncAllOperationsTypes = async (): Promise<any[] | null> => {
  try {
    console.log('🔄 syncAllOperationsTypes aufgerufen');
    
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_ALL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Alle Operations-Typen geladen:', data);
      return data;
    } else {
      const errorText = await response.text();
      console.error('❌ Failed to sync all operations types:', response.status, errorText);
      return null;
    }
  } catch (error) {
    console.error('❌ Error syncing all operations types:', error);
    return null;
  }
};

// Einsatztyp zur Organisation hinzufügen
export const addOperationsTypeWithCacheClear = async (opsTypeID: number): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for adding operations type');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_ADD_TO_ORG), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        orgID, 
        opsTypeID 
      })
    });

    if (response.ok) {
      clearOperationsTypesCache(); // Nur OperationsTypes Cache löschen
      return true;
    } else {
      console.error('Failed to add operations type:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error adding operations type:', error);
    return false;
  }
};

// Einsatztyp von Organisation entfernen
export const deleteOperationsTypeWithCacheClear = async (opsTypeID: number): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for deleting operations type');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.OPERATIONS_TYPES_REMOVE_FROM_ORG), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        orgID, 
        opsTypeID 
      })
    });

    if (response.ok) {
      clearOperationsTypesCache(); // Nur OperationsTypes Cache löschen
      return true;
    } else if (response.status === 404) {
      // 404 bedeutet, dass der Einsatztyp nicht gefunden wurde (möglicherweise bereits entfernt)
      clearOperationsTypesCache(); // Nur OperationsTypes Cache löschen
      return true;
    } else {
      console.error('Failed to delete operations type:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error deleting operations type:', error);
    return false;
  }
};

// Tasks synchronisieren
export const syncTasks = async (): Promise<any[] | null> => {
  try {
    // Prüfen, ob Cache gültig ist
    if (isCacheValid(tasksCache)) {
      return tasksCache!.data;
    }

    const orgID = await getOrgID();
    if (!orgID) {
      console.error('No orgID available for tasks sync');
      return null;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TASKS), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID })
    });

    if (response.ok) {
      const data = await response.json();
      tasksCache = { data, timestamp: Date.now(), orgID };
      
      // Lokalen Cache aktualisieren
      localStorage.setItem('tasksWithIds', JSON.stringify(data));
      localStorage.setItem('standardTasks', JSON.stringify(data.map((t: any) => t.name)));
      
      return data;
    } else {
      const errorText = await response.text();
      console.error('Failed to sync tasks:', response.status, errorText);
      return null;
    }
  } catch (error) {
    console.error('Error syncing tasks:', error);
    return null;
  }
};

// Task hinzufügen
export const addTaskWithCacheClear = async (name: string): Promise<boolean> => {
  try {
    const orgID = await getOrgID();
    const userID = await getUserID();
    
    if (!orgID || !userID) {
      console.error('No orgID or userID available for adding task');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TASKS_ADD), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID, name })
    });

    if (response.ok) {
      tasksCache = null; // Cache löschen
      return true;
    } else {
      console.error('Failed to add task:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error adding task:', error);
    return false;
  }
};

// Task löschen
let deleteTaskInProgress = new Set<number>();

export const deleteTaskWithCacheClear = async (taskID: number): Promise<boolean> => {
  // Verhindere doppelte Aufrufe für denselben taskID
  if (deleteTaskInProgress.has(taskID)) {
    console.warn(`Delete task ${taskID} already in progress, skipping duplicate call`);
    return false;
  }
  
  deleteTaskInProgress.add(taskID);
  
  // Timeout nach 30 Sekunden, um stuck states zu verhindern
  const timeoutId = setTimeout(() => {
    deleteTaskInProgress.delete(taskID);
    console.warn(`Delete task ${taskID} timeout, removing from progress set`);
  }, 30000);
  
  try {
    const orgID = await getOrgID();
    
    if (!orgID) {
      console.error('No orgID available for deleting task');
      return false;
    }

    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TASKS_DELETE), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgID, taskID })
    });

    if (response.ok) {
      tasksCache = null; // Cache löschen
      return true;
    } else if (response.status === 404) {
      // 404 bedeutet, dass der Task nicht gefunden wurde (möglicherweise bereits entfernt)
      tasksCache = null; // Cache löschen
      return true;
    } else {
      console.error('Failed to delete task:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error deleting task:', error);
    return false;
  } finally {
    // Immer den taskID aus dem Set entfernen, auch bei Fehlern
    clearTimeout(timeoutId);
    deleteTaskInProgress.delete(taskID);
  }
};

// Vollständige Synchronisation
export const fullSync = async (): Promise<void> => {
  
  try {
    await Promise.all([
      syncLocations(),
      syncRadioChannels(),
      syncFeedbacks(),
      syncOperationsTypes(),
      syncTasks()
    ]);
    
  } catch (error) {
    console.error('Full sync failed:', error);
    throw error;
  }
}; 